<template lang="pug">
v-card.pa-6.mb-6.rounded-lg(outlined)
  v-row(no-gutters)
    v-col.mr-4
      .text-sm.font-semibold.neutral700--text.mb-1 Remarks
      div.text-sm.mt-2(v-if="isLocked") {{ remarks || 'No remarks.' }}
      v-textarea.text-sm.mt-2.round-xl(v-else v-model="remarks" placeholder="Write your remarks here" outlined rows="6" hide-details="auto")
    v-col
      div.d-flex.align-center
        div.text-sm.font-semibold.neutral700--text Attachment
        v-spacer
        div.file-layout(v-if="files.length && !hasFileUploadedBefore && !isLocked")
          y-button(label="Add files" outlined size="xs" button-class="px-2" append-icon="$yzi-upload-01" icon-size="15")
          input.input-layout(type="file" @change="onFileChange" accept="image/png,image/jpeg,.pdf,.doc" style="width: 92px !important" multiple="multiple")
      template(v-if="files.length" )
        v-card.round-xl.mt-2(outlined style="border-style: solid; border-color: var(--v-neutral400)")
          v-simple-table
            thead
              tr
                th.text-xs Name
                th.text-xs(style="width: 120px;") Upload date
                th.text-xs File type
                th.text-xs
            tbody
              tr(v-for="(file, index) in files" :key="index")
                td.text-sm.py-2(style="word-break: break-word;") {{ file.name }}
                td.text-sm {{ getTodayDate() }}
                td.text-sm {{ file.type }}
                td.text-sm 
                  v-btn(v-if="!parentData" small icon @click="removeFile(index)")
                    v-icon(size="18") $yzi-trash-03
        div.d-flex.justify-end.mt-2(v-if="hasFileUploadedBefore")
          y-button(:label="getAttachmentButtonText()" underline size="xs" @click="openDocumentAttachmentDialog = true")

      .text-sm.mt-2(v-else-if="isLocked") No attachments.
      v-card.round-xl.pa-8.mt-2(v-else link outlined style="border-style: dashed; border-color: var(--v-neutral400)")
        .text-center
          v-avatar.mb-3(color="neutral300" size="40")
            v-icon(color="neutral500" size="18") $yzi-upload-01
          .mb-1.text-sm
            span.font-semibold.info--text Click to upload
            | or drag and drop
          .text-xs.neutral500--text PDF, DOCS, JPG, PNG (max. 4MB)
        input.input-change-layout(ref="file" type="file" accept="image/png,image/jpeg,.pdf,.doc" multiple @change="onFileChange")
  .dialogs
    document-attachment-dialog(v-if="openDocumentAttachmentDialog" :open.sync="openDocumentAttachmentDialog" :parentData="parentData" :selectedDocument="selectedDocument" @updated="fetchAttachments")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { isFileSizeOverLimit } from '@/utils'
import DocumentAttachmentDialog from '@/components/clinic/dialogs/purchasehub/DocumentAttachmentDialog.vue'

export default {
  components: {
    DocumentAttachmentDialog,
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    parentData: {
      type: Object,
      default: null
    },
    selectedDocument: {
      type: Object,
      required: true
    },
  },
  data() {
    return {
      showDeleteButton: false,
      remarks: null,
      files: [],
      filesCount: 0,
      hasFileUploadedBefore: false,
      openDocumentAttachmentDialog: false,
    }
  },
  mounted () {
    if (this.parentData) {
      this.remarks = this.parentData.remarks
      this.fetchAttachments()
    }
  },
  watch: {
    remarks (newValue) {
      this.$emit('input', { ...this.value, remarks: newValue })
    },
    files (newValue) {
      this.$emit('input', { ...this.value, attachment: newValue })
    },
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    status () {
      return this.parentData?.request_status || this.parentData?.order_status
    },
    isArchived () {
      return this.status === 'archived'
    },
    isCompleted () {
      return this.status === 'completed'
    },
    isCreated () {
      return this.status === 'created'
    },
    isLocked () {
      if (this.isArchived || this.isCompleted || this.isCreated) return true
      if (this.selectedDocument.id === 'stock-transfer' && ['requested', 'rejected'].includes(this.status)) return true
      return false
    },
    attachmentParams () {
      if (!this.parentData) return {}

      const params = {
        page_size: 2
      }

      switch (this.selectedDocument.id) {
        case 'quotation':
          return {
            ...params,
            quotation: this.parentData.id
          }

        case 'purchase-order':
        case 'stock-transfer':
          return {
            ...params,
            order: this.parentData.id
          }

        case 'invoice':
          return {
            ...params,
            invoice: this.parentData.id
          }

        default:
          return params
      }
    },
  },
  methods: {
    ...mapActions(['showMessage']),
    async onFileChange (e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) {
        return
      }
      if (isFileSizeOverLimit(files[0], 5)) {
        this.showMessage(['error', this.$t('error_message.file_reached_limit')])
        return
      }
      files.forEach(o => {
        this.files.push(o)
      })
    },
    getTodayDate () {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return this.$options.filters.humanizeDate(`${year}-${month}-${day}`)
    },
    removeFile (idx) {
      this.files.splice(idx, 1)
    },
    async fetchAttachments () {
      try {
        const { data } = await this.axios.get(`/purchases/${this.company.slug}/attachments`, { params: this.attachmentParams })
        this.files = data.results.map(file => {
          return {
            name: file.document.filename,
            date: this.$options.filters.humanizeDate(file.created),
            type: file.document.type,
            ...file
          }
        })
        this.filesCount = data.count
        if (data.count > 0) {
          this.hasFileUploadedBefore = true
        }
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    getAttachmentButtonText () {
      let text = 'Edit or add more'
      if (this.filesCount > 2) {
        let count = this.filesCount - 2
        text = `+${count} more files. Show all`
      }
      return text
    }
  },
}
</script>

<style scoped>
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
.file-layout {
  position: relative;
}
.input-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
  cursor: pointer;
}
</style>